import { AddDashboardFiltersToAnalysisCheckbox } from '@modules/analysis/components/SaveDashboardFiltersToAnalysisCheckbox';
import { useDashboardToAddToFromParam } from '@modules/analysis/hooks';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { AnalysisActionType } from '@modules/analysis/types';
import { SaveArtefact } from '@modules/common/components/SaveArtefact';
import { SaveArtefactConfirmDialog } from '@modules/common/components/SaveArtefact/SaveArtefactConfirmDialog';
import {
  useGetDashboardTypeDisplayNameFromURLParam,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { ArtefactType } from '@modules/common/types';
import { useSaveFeedAnalysis } from '@modules/feed/hooks';
import { useFeedStore } from '@modules/feed/store/feedStore';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import ControlPointDuplicateRoundedIcon from '@mui/icons-material/ControlPointDuplicateRounded';
import { LoadingButton } from '@mui/lab';
import { Stack, Tooltip } from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import { useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { Scope } from 'v2/core/entities/scope';
import { shallow } from 'zustand/shallow';

const trackSaveAsNewCTAEvents = createAnalyticsTracker('saveAsNewCTA');

interface SaveSearchActionProps {
  actionType: AnalysisActionType;
  shouldSaveDashboardFilters?: boolean;
  setShouldSaveDashboardFilters?: (value: boolean) => void;
}

export const SaveSearchAction = ({
  actionType,
  shouldSaveDashboardFilters,
  setShouldSaveDashboardFilters,
}: SaveSearchActionProps) => {
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const { title, viewToken } = useAnalysisStore(
    ({ title, viewToken }) => ({
      title,
      viewToken,
    }),
    shallow,
  );
  const [isSaveArtefactFormOpen, setIsSaveArtefactFormOpen] = useState(false);
  const [artefactRoute, setSaveArtefactRoute] = useState<string | null>(null);

  const { dashboardToAddToFromParam } = useDashboardToAddToFromParam();
  const dashboardTypeDisplayName = useGetDashboardTypeDisplayNameFromURLParam();

  const { onSaveSearchDashboard } = useSaveFeedAnalysis({
    shouldSaveDashboardFilters,
  });

  const dashboardFilters = useFeedStore(
    ({ visual }) => visual.dashboardFilters,
  );

  const closeForm = () => setIsSaveArtefactFormOpen(false);
  const openForm = () => {
    setIsSaveArtefactFormOpen(true);
    if (actionType === AnalysisActionType.SAVE_AS) {
      trackSaveAsNewCTAEvents({
        eventName: 'Save As New CTA Clicked - Search page',
        artefactName: title,
        artefactViewToken: viewToken,
        isUserArtefactOwner: false,
      });
    }
  };

  const onSaveSearchArtefact = async ({
    name,
    scope,
    shouldShowAsFeed,
  }: {
    name: string;
    scope: Scope;
    shouldShowAsFeed?: boolean;
  }) => {
    const artefactDetails = await onSaveSearchDashboard({
      name,
      scope,
      shouldShowAsFeed,
      shouldRedirect: false,
    });
    setSaveArtefactRoute(artefactDetails.artefactRoute);

    return artefactDetails;
  };

  const saveText = dashboardToAddToFromParam
    ? `Save & add to ${dashboardTypeDisplayName}`
    : 'Save';

  const buttonText =
    actionType === AnalysisActionType.SAVE ? saveText : 'Save as new';

  const ButtonIcon =
    actionType === AnalysisActionType.SAVE
      ? BookmarkRoundedIcon
      : ControlPointDuplicateRoundedIcon;

  const headerText =
    actionType === AnalysisActionType.SAVE_AS
      ? `Save New ${isSearchToFeedEnabled ? 'Feed' : 'Search'}`
      : undefined;

  return (
    <>
      <Tooltip
        title={`Save new ${isSearchToFeedEnabled ? 'Feed' : 'Search'}`}
        placement="bottom"
        disableInteractive
      >
        <Stack>
          <LoadingButton
            variant="outlined"
            onClick={openForm}
            startIcon={<ButtonIcon />}
            loadingPosition="start"
          >
            {buttonText}
          </LoadingButton>
        </Stack>
      </Tooltip>
      <SaveArtefactConfirmDialog
        artefactRoute={artefactRoute}
        fullWidth
        maxWidth="sm"
        PaperProps={{ elevation: 3 }}
        open={isSaveArtefactFormOpen}
        onClose={closeForm}
      >
        <SaveArtefact
          headerText={headerText}
          buttonText={
            actionType === AnalysisActionType.SAVE_AS
              ? `Save as new`
              : undefined
          }
          onSubmit={onSaveSearchArtefact}
          onClose={closeForm}
          artefactType={ArtefactType.Feed}
          shouldIncludeDashboardFilters={shouldSaveDashboardFilters}
          isSaveToDashboardAction={!!dashboardToAddToFromParam}
          secondaryActionNode={
            !!dashboardFilters?.whereClause &&
            !!setShouldSaveDashboardFilters ? (
              <AddDashboardFiltersToAnalysisCheckbox
                useCase={AnalysisUseCase.Feed}
                checked={shouldSaveDashboardFilters ?? false}
                onChange={setShouldSaveDashboardFilters}
              />
            ) : undefined
          }
        />
      </SaveArtefactConfirmDialog>
    </>
  );
};
